<script setup lang="ts">
import { NSpin, useMessage } from "naive-ui";
import { computed, onMounted, onUnmounted, reactive, watchEffect } from "vue";
import { requestFile } from "../services/cms";
import { downloadBlob } from "../utils/file";

const message = useMessage();

const props = defineProps<{
    isOpen: boolean;
    url: string;
    name: string;
}>();

const emit = defineEmits<{
    close: [];
}>();

const state = reactive({
    loaded: false,
    dataUrl: null as string | null,
});

const isPdf = computed(() => {
    const extension = props.name.split(".").pop()?.toLowerCase();
    return extension === "pdf";
});

watchEffect(() => {
    if (props.isOpen) {
        state.loaded = false;
        load();
    }
});

async function load() {
    try {
        const blob = await requestFile(props.url);
        if (isPdf.value) {
            const pdfBlob = new Blob([blob], { type: "application/pdf" });
            state.dataUrl = URL.createObjectURL(pdfBlob);
            state.loaded = true;
        } else {
            const reader = new FileReader();
            reader.onloadend = () => {
                state.dataUrl = reader.result as string;
                state.loaded = true;
            };
            reader.onerror = () => {
                console.error("FileReader error:", reader.error);
                state.loaded = false;
            };
            reader.readAsDataURL(blob);
        }
    } catch (error) {
        message.error(`Error loading media: ${error}`);
        state.loaded = false;
    }
}

async function download() {
    try {
        const blob = await requestFile(props.url);
        downloadBlob(props.name, blob);
    } catch (error) {
        message.error(`Error downloading media: ${error}`);
    }
}

async function openInNewTab() {
    if (!state.dataUrl) {
        return;
    }
    const newWindow = window.open();
    if (newWindow) {
        newWindow.document.write(`
            <iframe src="${state.dataUrl}" style="width:100%; height:100%; border:none;"></iframe>
        `);
        newWindow.document.title = props.name;
    }
}

function handleKeydown(event: KeyboardEvent) {
    if (event.key === "Escape") {
        emit("close");
    }
}

onMounted(() => {
    window.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
    if (state.dataUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(state.dataUrl);
    }
    window.removeEventListener("keydown", handleKeydown);
});
</script>

<template>
    <Teleport to="body">
        <div
            v-if="isOpen"
            class="fixed inset-0 z-[2001] flex items-center justify-center bg-black bg-opacity-75"
        >
            <div
                class="relative h-full bg-white rounded-lg shadow-lg w-[calc(100%-70px)] overflow-hidden"
            >
                <div class="flex justify-between items-center p-4 border-b border-gray-200 gap-2">
                    <h2 class="text-xl font-semibold text-gray-dark">{{ name }}</h2>
                    <div class="flex gap-2">
                        <button
                            v-if="isPdf"
                            class="material-symbols-outlined text-sm text-gray-dark h-5 w-5 rounded-full flex items-center justify-center hover:bg-gray-200"
                            title="Open in new tab"
                            @click="openInNewTab"
                        >
                            open_in_new
                        </button>
                        <button
                            class="material-symbols-outlined text-sm text-gray-dark h-5 w-5 rounded-full flex items-center justify-center hover:bg-gray-200"
                            title="Download"
                            @click="download"
                        >
                            download
                        </button>
                        <button
                            class="material-symbols-outlined text-sm text-gray-dark h-5 w-5 rounded-full flex items-center justify-center hover:bg-dark-mode-red"
                            title="Close"
                            @click="emit('close')"
                        >
                            close
                        </button>
                    </div>
                </div>
                <div class="p-4 flex items-center justify-center w-full h-[calc(100vh-60px)]">
                    <div v-if="isPdf && state.loaded && state.dataUrl" class="w-full h-full">
                        <iframe
                            :src="state.dataUrl || ''"
                            class="w-full h-full"
                            sandboxed="allow-scripts allow-same-origin"
                        >
                        </iframe>
                    </div>
                    <img
                        v-else-if="state.loaded && state.dataUrl"
                        :src="state.dataUrl"
                        :alt="name"
                        class="w-full h-full object-contain"
                    />
                    <div v-if="!state.loaded" class="p-12 flex items-center justify-center">
                        <n-spin size="large" />
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>
